import { type TFunction } from 'i18next';
import { generatePath } from 'react-router';

import { ABTestVariantInfo } from 'src/services/insights/types';
import { Routes } from 'src/utils/enums';

export type ABTestTooltipVariant = Pick<
  ABTestVariantInfo,
  | 'ab_test_status'
  | 'end_condition_type'
  | 'end_date'
  | 'sample_count'
  | 'start_date'
>;

export function getAbTestDetailPath(abTestConfigId: string): string {
  return generatePath(Routes.AGENT_AB_TESTS_DETAIL, {
    abTestId: abTestConfigId,
  });
}

export function formatShortDate(isoDate: string | null): string {
  if (!isoDate) {
    return '—';
  }
  return new Date(isoDate).toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

/**
 * Returns true if any variant is part of a draft or running A/B test.
 */
export function hasAbTestBlockingDelete(
  variants: ABTestVariantInfo[] | undefined,
): boolean {
  return (
    variants?.some(
      v => v.ab_test_status === 'running' || v.ab_test_status === 'draft',
    ) ?? false
  );
}

export function getAbTestTooltip(
  t: TFunction<'solve-insights'>,
  variant: ABTestTooltipVariant,
): string {
  if (variant.ab_test_status === 'canceled') {
    return t('common.abTest.pillLabel', {
      status: t('common.abTest.status.cancelled'),
    });
  }

  const isActive = variant.ab_test_status === 'running';
  const statusLabel = isActive
    ? t('common.abTest.status.active')
    : t('common.abTest.status.completed');

  if (variant.end_condition_type === 'sample_count' && variant.sample_count) {
    if (isActive) {
      return t('common.abTest.tooltipEndsAfter', {
        sampleCount: variant.sample_count.toLocaleString(),
        status: statusLabel,
      });
    }
    return t('common.abTest.tooltipSampleCount', {
      sampleCount: variant.sample_count.toLocaleString(),
      status: statusLabel,
    });
  }

  return t('common.abTest.tooltipDateRange', {
    end: formatShortDate(variant.end_date),
    start: formatShortDate(variant.start_date),
    status: statusLabel,
  });
}
